import React from "react";
import { connect } from "react-redux";
import Loader from "../../../components/Loader/Loader";
import {
  getPlanData,
  updatePlan,
  createPlan,
  deletePlan,
  togglePlanStatus,
} from "../../../actions/planManagerActions";

const EMPTY_CREATE_FORM = {
  plan_type: "individual",
  plan_name: "",
  plan_code: "",
  monthly: "",
  yearly: "",
  stripe_plan_monthly: "",
  stripe_plan_yearly: "",
  no_of_calendars: "1",
  no_of_seats: "1",
  no_of_events: "-1",
  plan_grace_period: "15",
};

class PlanManager extends React.Component {
  state = {
    editingPlanId: null,
    editForm: {},
    editFeatures: {},
    successMsg: "",
    errorMsg: "",
    showCreateForm: false,
    createForm: { ...EMPTY_CREATE_FORM },
    createFeatures: {},
    deleteTarget: null,
  };

  componentDidMount() {
    this.props.getPlanData(0);
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.updateSuccess && this.props.updateSuccess) {
      this.setState({ editingPlanId: null, successMsg: "Plan updated successfully!" });
      this.props.getPlanData(0);
      this.clearMsgSoon();
    }
  }

  clearMsgSoon = () => {
    setTimeout(() => this.setState({ successMsg: "", errorMsg: "" }), 4000);
  };

  refresh = (msg) => {
    this.props.getPlanData(0);
    this.setState({ successMsg: msg, errorMsg: "" });
    this.clearMsgSoon();
  };

  showError = (msg) => {
    this.setState({ errorMsg: msg, successMsg: "" });
    this.clearMsgSoon();
  };

  // ── Edit ──────────────────────────────────────────────────────────────
  startEdit = (plan) => {
    const featureMap = {};
    if (plan.features) {
      plan.features.forEach((f) => {
        featureMap[f.feature_id] = f.status;
      });
    }
    this.setState({
      editingPlanId: plan.id,
      showCreateForm: false,
      editForm: {
        monthly: plan.monthly || "",
        yearly: plan.yearly || "",
        stripe_plan_monthly: plan.stripe_plan_monthly || "",
        stripe_plan_yearly: plan.stripe_plan_yearly || "",
        no_of_calendars: plan.no_of_calendars || "",
        no_of_seats: plan.no_of_seats || "",
        no_of_events: plan.no_of_events || "",
        plan_grace_period: plan.plan_grace_period || "",
      },
      editFeatures: featureMap,
    });
  };

  cancelEdit = () => {
    this.setState({ editingPlanId: null, editForm: {}, editFeatures: {} });
  };

  handleChange = (field, value) => {
    this.setState((prev) => ({
      editForm: { ...prev.editForm, [field]: value },
    }));
  };

  toggleFeature = (featureId) => {
    this.setState((prev) => ({
      editFeatures: {
        ...prev.editFeatures,
        [featureId]: prev.editFeatures[featureId] === "E" ? "D" : "E",
      },
    }));
  };

  savePlan = () => {
    const { editingPlanId, editForm, editFeatures } = this.state;
    const features = Object.keys(editFeatures).map((fId) => ({
      feature_id: parseInt(fId),
      status: editFeatures[fId],
    }));

    this.props.updatePlan({
      id: editingPlanId,
      monthly_price: editForm.monthly,
      annual_price: editForm.yearly,
      stripe_plan_monthly: editForm.stripe_plan_monthly,
      stripe_plan_yearly: editForm.stripe_plan_yearly,
      no_of_calendars: editForm.no_of_calendars,
      no_of_seats: editForm.no_of_seats,
      no_of_events: editForm.no_of_events,
      plan_grace_period: editForm.plan_grace_period,
      features: features,
    });
  };

  // ── Create ────────────────────────────────────────────────────────────
  startCreate = () => {
    this.setState({
      showCreateForm: true,
      editingPlanId: null,
      createForm: { ...EMPTY_CREATE_FORM },
      createFeatures: {},
      errorMsg: "",
    });
  };

  cancelCreate = () => {
    this.setState({ showCreateForm: false, createForm: { ...EMPTY_CREATE_FORM }, createFeatures: {} });
  };

  handleCreateChange = (field, value) => {
    this.setState((prev) => ({ createForm: { ...prev.createForm, [field]: value } }));
  };

  toggleCreateFeature = (featureId) => {
    this.setState((prev) => ({
      createFeatures: {
        ...prev.createFeatures,
        [featureId]: prev.createFeatures[featureId] === "E" ? "D" : "E",
      },
    }));
  };

  submitCreate = () => {
    const { createForm, createFeatures } = this.state;
    if (!createForm.plan_name.trim()) {
      this.showError("Plan name is required.");
      return;
    }
    const features = Object.keys(createFeatures).map((fId) => ({
      feature_id: parseInt(fId),
      status: createFeatures[fId],
    }));

    this.props.createPlan(
      {
        plan_type: createForm.plan_type,
        plan_name: createForm.plan_name,
        plan_code: createForm.plan_code,
        monthly_price: createForm.monthly,
        annual_price: createForm.yearly,
        stripe_plan_monthly: createForm.stripe_plan_monthly,
        stripe_plan_yearly: createForm.stripe_plan_yearly,
        no_of_calendars: createForm.no_of_calendars,
        no_of_seats: createForm.no_of_seats,
        no_of_events: createForm.no_of_events,
        plan_grace_period: createForm.plan_grace_period,
        features,
      },
      () => {
        this.setState({ showCreateForm: false, createForm: { ...EMPTY_CREATE_FORM }, createFeatures: {} });
        this.refresh("Plan created successfully!");
      },
      (msg) => this.showError(msg)
    );
  };

  // ── Activate / Deactivate / Delete ────────────────────────────────────
  handleToggleStatus = (plan) => {
    this.props.togglePlanStatus(
      plan.id,
      () => this.refresh(plan.status === "E" ? "Plan deactivated. It's now hidden from users." : "Plan activated and visible to users."),
      (msg) => this.showError(msg)
    );
  };

  confirmDelete = (plan) => this.setState({ deleteTarget: plan });
  cancelDelete = () => this.setState({ deleteTarget: null });

  handleDelete = () => {
    const target = this.state.deleteTarget;
    if (!target) return;
    this.props.deletePlan(
      target.id,
      () => {
        this.setState({ deleteTarget: null });
        this.refresh("Plan deleted successfully!");
      },
      (msg) => {
        this.setState({ deleteTarget: null });
        this.showError(msg);
      }
    );
  };

  // ── Render ────────────────────────────────────────────────────────────
  renderFeatureGrid = (featuresState, toggleFn) =>
    this.props.planManagerData?.features && (
      <div>
        <label style={{ ...labelStyle, marginBottom: 8, display: "block" }}>Features</label>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {this.props.planManagerData.features.map((feature) => (
            <label key={feature.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 0", cursor: "pointer", fontSize: 13 }}>
              <input
                type="checkbox"
                checked={featuresState[feature.id] === "E"}
                onChange={() => toggleFn(feature.id)}
                style={{ accentColor: "#f0932b" }}
              />
              <span>
                {String(feature.feature || "").replace(/\s+and iCloud/gi, "")}
                {(feature.feature === "Zapier" ||
                  String(feature.feature).includes("Zapier")) &&
                  !String(feature.feature).toLowerCase().includes("coming soon") && (
                  <span style={{ marginLeft: 6, color: "#f0932b", fontWeight: 600, fontSize: 12 }}>
                    (Coming Soon)
                  </span>
                )}
              </span>
            </label>
          ))}
        </div>
      </div>
    );

  renderCreateForm = () => {
    const { createForm } = this.state;
    return (
      <div style={{ border: "2px solid #27ae60", borderRadius: 8, padding: 20, marginBottom: 20, background: "#f4fdf7" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <h4 style={{ margin: 0, color: "#27ae60" }}>Add New Plan</h4>
          <div>
            <button onClick={this.submitCreate} disabled={this.props.actionInProgress}
              style={{ background: "#27ae60", color: "#fff", border: "none", padding: "8px 20px", borderRadius: 4, cursor: "pointer", marginRight: 8, fontWeight: 600 }}>
              {this.props.actionInProgress ? "Creating..." : "Create Plan"}
            </button>
            <button onClick={this.cancelCreate}
              style={{ background: "#ddd", color: "#333", border: "none", padding: "8px 20px", borderRadius: 4, cursor: "pointer" }}>
              Cancel
            </button>
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Plan Type</label>
            <select value={createForm.plan_type} onChange={(e) => this.handleCreateChange("plan_type", e.target.value)} style={inputStyle}>
              <option value="individual">Individual</option>
              <option value="team">Team / Organizations</option>
            </select>
          </div>
          <div>
            <label style={labelStyle}>Plan Name *</label>
            <input type="text" value={createForm.plan_name} onChange={(e) => this.handleCreateChange("plan_name", e.target.value)} style={inputStyle} placeholder="e.g. Premium" />
          </div>
          <div>
            <label style={labelStyle}>Plan Code</label>
            <input type="text" value={createForm.plan_code} onChange={(e) => this.handleCreateChange("plan_code", e.target.value)} style={inputStyle} placeholder="auto if empty" />
          </div>
          <div>
            <label style={labelStyle}>Grace Period (days)</label>
            <input type="text" value={createForm.plan_grace_period} onChange={(e) => this.handleCreateChange("plan_grace_period", e.target.value)} style={inputStyle} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Monthly Price ($)</label>
            <input type="text" value={createForm.monthly} onChange={(e) => this.handleCreateChange("monthly", e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Yearly Price ($)</label>
            <input type="text" value={createForm.yearly} onChange={(e) => this.handleCreateChange("yearly", e.target.value)} style={inputStyle} />
          </div>
          <div>
            <label style={labelStyle}>Stripe Monthly Price ID</label>
            <input type="text" value={createForm.stripe_plan_monthly} onChange={(e) => this.handleCreateChange("stripe_plan_monthly", e.target.value)} style={inputStyle} placeholder="price_xxx" />
          </div>
          <div>
            <label style={labelStyle}>Stripe Yearly Price ID</label>
            <input type="text" value={createForm.stripe_plan_yearly} onChange={(e) => this.handleCreateChange("stripe_plan_yearly", e.target.value)} style={inputStyle} placeholder="price_xxx" />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>Calendars</label>
            <input type="text" value={createForm.no_of_calendars} onChange={(e) => this.handleCreateChange("no_of_calendars", e.target.value)} style={inputStyle} placeholder="-1 for unlimited" />
          </div>
          <div>
            <label style={labelStyle}>Seats</label>
            <input type="text" value={createForm.no_of_seats} onChange={(e) => this.handleCreateChange("no_of_seats", e.target.value)} style={inputStyle} placeholder="-1 for unlimited" />
          </div>
          <div>
            <label style={labelStyle}>Events</label>
            <input type="text" value={createForm.no_of_events} onChange={(e) => this.handleCreateChange("no_of_events", e.target.value)} style={inputStyle} placeholder="-1 for unlimited" />
          </div>
        </div>

        {this.renderFeatureGrid(this.state.createFeatures, this.toggleCreateFeature)}
      </div>
    );
  };

  renderPlanRow = (plan) => {
    const isEditing = this.state.editingPlanId === plan.id;
    const { editForm } = this.state;

    if (isEditing) {
      return (
        <div key={plan.id} style={{ border: "2px solid #f0932b", borderRadius: 8, padding: 20, marginBottom: 16, background: "#fffdf5" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
            <h4 style={{ margin: 0, color: "#f0932b" }}>Editing: {plan.plan}</h4>
            <div>
              <button onClick={this.savePlan} disabled={this.props.updating}
                style={{ background: "#f0932b", color: "#fff", border: "none", padding: "8px 20px", borderRadius: 4, cursor: "pointer", marginRight: 8, fontWeight: 600 }}>
                {this.props.updating ? "Saving..." : "Save"}
              </button>
              <button onClick={this.cancelEdit}
                style={{ background: "#ddd", color: "#333", border: "none", padding: "8px 20px", borderRadius: 4, cursor: "pointer" }}>
                Cancel
              </button>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Monthly Price ($)</label>
              <input type="text" value={editForm.monthly} onChange={(e) => this.handleChange("monthly", e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Yearly Price ($)</label>
              <input type="text" value={editForm.yearly} onChange={(e) => this.handleChange("yearly", e.target.value)} style={inputStyle} />
            </div>
            <div>
              <label style={labelStyle}>Stripe Monthly Price ID</label>
              <input type="text" value={editForm.stripe_plan_monthly} onChange={(e) => this.handleChange("stripe_plan_monthly", e.target.value)}
                style={inputStyle} placeholder="price_xxx" />
            </div>
            <div>
              <label style={labelStyle}>Stripe Yearly Price ID</label>
              <input type="text" value={editForm.stripe_plan_yearly} onChange={(e) => this.handleChange("stripe_plan_yearly", e.target.value)}
                style={inputStyle} placeholder="price_xxx" />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
            <div>
              <label style={labelStyle}>Calendars</label>
              <input type="text" value={editForm.no_of_calendars} onChange={(e) => this.handleChange("no_of_calendars", e.target.value)}
                style={inputStyle} placeholder="-1 for unlimited" />
            </div>
            <div>
              <label style={labelStyle}>Seats</label>
              <input type="text" value={editForm.no_of_seats} onChange={(e) => this.handleChange("no_of_seats", e.target.value)}
                style={inputStyle} placeholder="-1 for unlimited" />
            </div>
            <div>
              <label style={labelStyle}>Events</label>
              <input type="text" value={editForm.no_of_events} onChange={(e) => this.handleChange("no_of_events", e.target.value)}
                style={inputStyle} placeholder="-1 for unlimited" />
            </div>
            <div>
              <label style={labelStyle}>Grace Period (days)</label>
              <input type="text" value={editForm.plan_grace_period} onChange={(e) => this.handleChange("plan_grace_period", e.target.value)}
                style={inputStyle} />
            </div>
          </div>

          {this.renderFeatureGrid(this.state.editFeatures, this.toggleFeature)}
        </div>
      );
    }

    const isActive = plan.status !== "D";
    return (
      <div key={plan.id} className="rd_panmanagerthincontablething rd_panmanagerthincontablething2"
        style={!isActive ? { opacity: 0.6, background: "#fafafa" } : {}}>
        <div className="rd_panmanagerthincontablethingitem rd_panmanager-plan-cell">
          <span>{plan.plan}</span>
          <span style={isActive ? activeBadge : inactiveBadge}>{isActive ? "Active" : "Hidden"}</span>
        </div>
        <div className="rd_panmanagerthincontablethingitem">{plan.monthly}$</div>
        <div className="rd_panmanagerthincontablethingitem">{plan.yearly}$</div>
        <div className="rd_panmanagerthincontablethingitem">
          {plan.stripe_plan_monthly ? (
            <span className="rd_panmanager-stripe-id" title={plan.stripe_plan_monthly}>
              {plan.stripe_plan_monthly}
            </span>
          ) : (
            <span className="rd_panmanager-stripe-id rd_panmanager-stripe-id--empty">not set</span>
          )}
        </div>
        <div className="rd_panmanagerthincontablethingitem">
          {plan.stripe_plan_yearly ? (
            <span className="rd_panmanager-stripe-id" title={plan.stripe_plan_yearly}>
              {plan.stripe_plan_yearly}
            </span>
          ) : (
            <span className="rd_panmanager-stripe-id rd_panmanager-stripe-id--empty">not set</span>
          )}
        </div>
        <div className="rd_panmanagerthincontablethingitem">
          <div className="rd_panmanager-actions">
            <button
              type="button"
              onClick={() => this.startEdit(plan)}
              className="rd_panmanager-action-btn rd_panmanager-action-btn--edit"
            >
              Edit
            </button>
            <button
              type="button"
              onClick={() => this.handleToggleStatus(plan)}
              className={`rd_panmanager-action-btn rd_panmanager-action-btn--toggle ${isActive ? "is-active" : "is-inactive"}`}
            >
              {isActive ? "Deactivate" : "Activate"}
            </button>
            <button
              type="button"
              onClick={() => this.confirmDelete(plan)}
              className="rd_panmanager-action-btn rd_panmanager-action-btn--delete"
            >
              Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  renderSection = (title, plans) => (
    <>
      <div
        className="rd_panmanagerthincontablething rd_panmanagerthincontablething1 rd_panmanager-section-title"
        style={title === "Teams" ? { marginTop: 16 } : {}}
      >
        <div className="rd_panmanagerthincontablethingitem">{title}</div>
      </div>
      <div className="rd_panmanagerthincontablething rd_panmanagerthincontablething1" style={{ background: "#f8f8f8" }}>
        <div className="rd_panmanagerthincontablethingitem" style={{ fontWeight: 600, fontSize: 13 }}>Plan</div>
        <div className="rd_panmanagerthincontablethingitem" style={{ fontWeight: 600, fontSize: 13 }}>Monthly</div>
        <div className="rd_panmanagerthincontablethingitem" style={{ fontWeight: 600, fontSize: 13 }}>Yearly</div>
        <div className="rd_panmanagerthincontablethingitem" style={{ fontWeight: 600, fontSize: 13 }}>Stripe Monthly ID</div>
        <div className="rd_panmanagerthincontablethingitem" style={{ fontWeight: 600, fontSize: 13 }}>Stripe Yearly ID</div>
        <div className="rd_panmanagerthincontablethingitem rd_panmanager-actions-header" style={{ fontWeight: 600, fontSize: 13 }}>
          Actions
        </div>
      </div>
      {plans.map((plan) => this.renderPlanRow(plan))}
    </>
  );

  render() {
    const { deleteTarget } = this.state;
    return (
      <div className="eventdetailsaddbox rd_noshadow">
        <Loader isShowLoader={this.props.showLoader} />
        <div className="rd_supertoptwocom">
          <div className="boxheader rd_floatingheaderthig">
            <div className="rd_inputselectheader" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
              <div className="rd_selectheaderrdt2 rd_selectheaderrdt2profile">
                <h3><strong>Plan Manager</strong></h3>
              </div>
              <button onClick={this.startCreate}
                style={{ background: "#27ae60", color: "#fff", border: "none", padding: "9px 22px", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
                + Add New Plan
              </button>
            </div>
          </div>
        </div>

        {this.state.successMsg && (
          <div style={{ background: "#d4edda", color: "#155724", padding: "10px 16px", borderRadius: 4, marginBottom: 12, fontWeight: 500 }}>
            {this.state.successMsg}
          </div>
        )}
        {this.state.errorMsg && (
          <div style={{ background: "#f8d7da", color: "#721c24", padding: "10px 16px", borderRadius: 4, marginBottom: 12, fontWeight: 500 }}>
            {this.state.errorMsg}
          </div>
        )}

        {this.state.showCreateForm && this.renderCreateForm()}

        {this.props.PlanManagerSuccess && (
          <div className="rd_plansmanagercont">
            <div className="rd_panmanagerthincon">
              {this.renderSection("Individual", this.props.planManagerData?.individual || [])}
              {this.renderSection("Teams", this.props.planManagerData?.teams || [])}
            </div>
          </div>
        )}

        {deleteTarget && (
          <div style={overlayStyle} onClick={this.cancelDelete}>
            <div style={dialogStyle} onClick={(e) => e.stopPropagation()}>
              <h4 style={{ marginTop: 0, color: "#e74c3c" }}>Delete Plan</h4>
              <p style={{ color: "#555" }}>
                Are you sure you want to permanently delete <strong>{deleteTarget.plan}</strong>? This cannot be undone.
              </p>
              <p style={{ fontSize: 13, color: "#888" }}>
                Tip: if you only want to hide it from users, use <strong>Deactivate</strong> instead.
              </p>
              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 16 }}>
                <button onClick={this.cancelDelete} style={{ background: "#ddd", color: "#333", border: "none", padding: "8px 20px", borderRadius: 4, cursor: "pointer" }}>
                  Cancel
                </button>
                <button onClick={this.handleDelete} disabled={this.props.actionInProgress}
                  style={{ background: "#e74c3c", color: "#fff", border: "none", padding: "8px 20px", borderRadius: 4, cursor: "pointer", fontWeight: 600 }}>
                  {this.props.actionInProgress ? "Deleting..." : "Delete"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

const labelStyle = { fontSize: 12, fontWeight: 600, color: "#555", marginBottom: 4, display: "block" };
const inputStyle = { width: "100%", padding: "8px 10px", border: "1px solid #ddd", borderRadius: 4, fontSize: 13, boxSizing: "border-box" };
const activeBadge = { fontSize: 10, fontWeight: 700, color: "#155724", background: "#d4edda", padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap" };
const inactiveBadge = { fontSize: 10, fontWeight: 700, color: "#721c24", background: "#f8d7da", padding: "2px 8px", borderRadius: 10, whiteSpace: "nowrap" };
const overlayStyle = { position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1050 };
const dialogStyle = { background: "#fff", borderRadius: 8, padding: 24, width: 420, maxWidth: "90%", boxShadow: "0 8px 30px rgba(0,0,0,.2)" };

const mapStateToProps = (state) => ({
  PlanManagerSuccess: state.planManager.PlanManagerSuccess,
  IsError: state.planManager.IsError,
  showLoader: state.planManager.showLoader,
  planManagerData: state.planManager.planManagerData,
  updating: state.planManager.updating,
  updateSuccess: state.planManager.updateSuccess,
  actionInProgress: state.planManager.actionInProgress,
});

const actionMethods = {
  getPlanData,
  updatePlan,
  createPlan,
  deletePlan,
  togglePlanStatus,
};

export default connect(mapStateToProps, actionMethods)(PlanManager);
